import React, { useState, useRef, useEffect, useCallback } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Search01Icon, AddCircleIcon, ArrowDown01Icon, Cancel01Icon,
  NecklaceIcon, EarRingsIcon, GemIcon, GymnasticRingsIcon,
  DiamondIcon, Diamond01Icon, Delete02Icon,
  Invoice02Icon, PencilEdit02Icon, PackageIcon, GridViewIcon, ArrowRight01Icon, Copy01Icon, Watch01Icon,
} from "@hugeicons/core-free-icons";

// ─── Design tokens (mirrors JewelleryBilling) ─────────────────────────────────
const T = {
  gold: "#5B7CFA", goldLight: "#EEF2FF", goldMid: "#4363E8", goldDark: "#314CCB",
  bg: "#FFFFFF", surface: "#FFFFFF", surfaceHigh: "#F8FAFC",
  surfaceBorder: "#E2E8F0", surfaceBorderHigh: "#CBD5E1",
  text: "#0F172A", textMuted: "#475569", textDim: "#94A3B8",
  blue: "#2563EB", blueLight: "#EFF6FF",
  green: "#16A34A", greenLight: "#F0FDF4",
  red: "#DC2626", redLight: "#FEF2F2",
  amber: "#D97706", amberLight: "#FFFBEB",
  purple: "#6366F1", purpleLight: "#EEF2FF",
};

const HI = ({ icon, size = 14 }) => (
  <HugeiconsIcon icon={icon} size={size} color="currentColor" strokeWidth={1.5} />
);

function Btn({ children, variant = "ghost", onClick, style: s, small }) {
  const [hov, setHov] = useState(false);
  const vs = {
    ghost:   { bg: hov ? T.surfaceHigh : "transparent", border: T.surfaceBorderHigh, color: T.text },
    primary: { bg: hov ? T.goldDark : T.gold,           border: T.gold,              color: "#fff"  },
    danger:  { bg: hov ? "#3D1818" : T.redLight,        border: T.red,               color: T.red   },
  };
  const v = vs[variant];
  return (
    <button onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: small ? "4px 10px" : "7px 14px", background: v.bg, border: `0.5px solid ${v.border}`, borderRadius: 8, color: v.color, fontSize: small ? 11 : 12, fontWeight: 500, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.15s", whiteSpace: "nowrap", ...s }}>
      {children}
    </button>
  );
}

const fmt  = (n) => "₹" + Math.round(n).toLocaleString("en-IN");
const fmtW = (n) => parseFloat(n).toFixed(2) + "g";

// ─── Location config ───────────────────────────────────────────────────────────
const LOC = {
  Floor:    { color: T.green,  bg: T.greenLight,  label: "Floor"    },
  Vault:    { color: T.blue,   bg: T.blueLight,   label: "Vault"    },
  Karigar:  { color: T.amber,  bg: T.amberLight,  label: "Karigar"  },
  Approval: { color: T.purple, bg: T.purpleLight, label: "Approval" },
  Overdue:  { color: T.red,    bg: T.redLight,    label: "Overdue"  },
};

function getCatIcon(cat) {
  if (cat === "Rings")    return GemIcon;
  if (cat === "Bangles")  return GymnasticRingsIcon;
  if (cat === "Earrings") return EarRingsIcon;
  if (cat === "Pendant")  return DiamondIcon;
  if (cat === "Chain" || cat === "Necklace" || cat === "Sets") return NecklaceIcon;
  return Diamond01Icon;
}

// ─── Mock inventory data ───────────────────────────────────────────────────────
const INV = [
  // ── Floor ──────────────────────────────────────────────────────────────────
  { id: 1,  tag: "TG-2026-001", product: "Gold Bangle Set (Kada)",      cat: "Bangles",  purity: "91.6", gwt: 48.2, swt: 0,   nwt: 46.5, making: 750,  stoneVal: 0,      costRate: 8200, location: "Floor",    daysIn: 45,  added: "09 Jan 2026", karigar: null,           customer: null,         overdue: false },
  { id: 2,  tag: "TG-2026-002", product: "Diamond Necklace Set",         cat: "Necklace", purity: "91.6", gwt: 22.8, swt: 2.1, nwt: 20.7, making: 1200, stoneVal: 85000,  costRate: 8300, location: "Floor",    daysIn: 12,  added: "12 Mar 2026", karigar: null,           customer: null,         overdue: false },
  { id: 3,  tag: "TG-2026-003", product: "Gold Ring Ladies (Floral)",    cat: "Rings",    purity: "91.6", gwt: 5.4,  swt: 0,   nwt: 5.2,  making: 900,  stoneVal: 0,      costRate: 8150, location: "Floor",    daysIn: 67,  added: "19 Dec 2025", karigar: null,           customer: null,         overdue: false },
  { id: 4,  tag: "TG-2026-004", product: "Gold Chain 18in (Figaro)",     cat: "Chain",    purity: "91.6", gwt: 12.6, swt: 0,   nwt: 12.3, making: 550,  stoneVal: 0,      costRate: 8300, location: "Floor",    daysIn: 23,  added: "29 Feb 2026", karigar: null,           customer: null,         overdue: false },
  { id: 6,  tag: "TG-2026-006", product: "Gold Earrings Jhumka",         cat: "Earrings", purity: "91.6", gwt: 9.8,  swt: 0,   nwt: 9.5,  making: 950,  stoneVal: 0,      costRate: 8250, location: "Floor",    daysIn: 34,  added: "19 Feb 2026", karigar: null,           customer: null,         overdue: false },
  { id: 8,  tag: "TG-2026-008", product: "Silver Anklet Set",             cat: "Anklets",  purity: "—",    gwt: 38.5, swt: 0,   nwt: 37.8, making: 180,  stoneVal: 0,      costRate: 104,  location: "Floor",    daysIn: 89,  added: "27 Dec 2025", karigar: null,           customer: null,         overdue: false },
  { id: 12, tag: "TG-2026-012", product: "Gold Ring Gents (Band)",        cat: "Rings",    purity: "91.6", gwt: 8.2,  swt: 0,   nwt: 8.0,  making: 700,  stoneVal: 0,      costRate: 7950, location: "Floor",    daysIn: 156, added: "21 Oct 2025", karigar: null,           customer: null,         overdue: false },
  { id: 13, tag: "TG-2026-013", product: "Gold Mangalsutra Chain",        cat: "Chain",    purity: "91.6", gwt: 15.4, swt: 0,   nwt: 15.1, making: 600,  stoneVal: 0,      costRate: 8220, location: "Floor",    daysIn: 18,  added: "06 Mar 2026", karigar: null,           customer: null,         overdue: false },
  { id: 14, tag: "TG-2026-014", product: "Gold Bangle Fancy (Pair)",      cat: "Bangles",  purity: "75",   gwt: 22.6, swt: 0,   nwt: 21.9, making: 1100, stoneVal: 0,      costRate: 6800, location: "Floor",    daysIn: 41,  added: "12 Feb 2026", karigar: null,           customer: null,         overdue: false },
  { id: 15, tag: "TG-2026-015", product: "Pearl Earrings (Gold)",         cat: "Earrings", purity: "91.6", gwt: 6.2,  swt: 1.8, nwt: 4.4,  making: 1400, stoneVal: 12000,  costRate: 8350, location: "Floor",    daysIn: 6,   added: "18 Mar 2026", karigar: null,           customer: null,         overdue: false },
  { id: 16, tag: "TG-2026-016", product: "Gold Nosepin Set (Pair)",       cat: "Rings",    purity: "91.6", gwt: 2.1,  swt: 0,   nwt: 2.0,  making: 800,  stoneVal: 0,      costRate: 8200, location: "Floor",    daysIn: 55,  added: "28 Jan 2026", karigar: null,           customer: null,         overdue: false },
  { id: 17, tag: "TG-2026-017", product: "Gold Bracelet Ladies",          cat: "Bracelet", purity: "91.6", gwt: 14.8, swt: 0,   nwt: 14.4, making: 850,  stoneVal: 0,      costRate: 8250, location: "Floor",    daysIn: 29,  added: "24 Feb 2026", karigar: null,           customer: null,         overdue: false },
  { id: 18, tag: "TG-2026-018", product: "Gold Coin 10g (Lakshmi)",       cat: "Pendant",  purity: "99.9", gwt: 10.1, swt: 0,   nwt: 10.0, making: 200,  stoneVal: 0,      costRate: 9100, location: "Floor",    daysIn: 72,  added: "11 Jan 2026", karigar: null,           customer: null,         overdue: false },
  { id: 19, tag: "TG-2026-019", product: "Silver Payal (Pair)",           cat: "Anklets",  purity: "—",    gwt: 52.4, swt: 0,   nwt: 51.2, making: 120,  stoneVal: 0,      costRate: 104,  location: "Floor",    daysIn: 38,  added: "14 Feb 2026", karigar: null,           customer: null,         overdue: false },
  // ── Vault ───────────────────────────────────────────────────────────────────
  { id: 5,  tag: "TG-2026-005", product: "Gold Necklace Set (Bridal)",    cat: "Sets",     purity: "91.6", gwt: 88.4, swt: 4.2, nwt: 84.2, making: 1400, stoneVal: 120000, costRate: 8100, location: "Vault",    daysIn: 5,   added: "19 Mar 2026", karigar: null,           customer: null,         overdue: false },
  { id: 9,  tag: "TG-2026-009", product: "Ruby Pendant Polki",            cat: "Pendant",  purity: "91.6", gwt: 11.2, swt: 0.8, nwt: 10.4, making: 1800, stoneVal: 45000,  costRate: 8200, location: "Vault",    daysIn: 8,   added: "16 Mar 2026", karigar: null,           customer: null,         overdue: false },
  { id: 20, tag: "TG-2026-020", product: "Diamond Bangles Set",           cat: "Bangles",  purity: "75",   gwt: 38.6, swt: 3.4, nwt: 35.2, making: 1600, stoneVal: 220000, costRate: 6900, location: "Vault",    daysIn: 14,  added: "10 Mar 2026", karigar: null,           customer: null,         overdue: false },
  { id: 21, tag: "TG-2026-021", product: "Gold Necklace Temple Set",      cat: "Necklace", purity: "91.6", gwt: 62.4, swt: 0,   nwt: 61.0, making: 1200, stoneVal: 0,      costRate: 8200, location: "Vault",    daysIn: 21,  added: "03 Mar 2026", karigar: null,           customer: null,         overdue: false },
  { id: 22, tag: "TG-2026-022", product: "Gold Bar 50g",                  cat: "Pendant",  purity: "99.9", gwt: 50.2, swt: 0,   nwt: 50.0, making: 100,  stoneVal: 0,      costRate: 9150, location: "Vault",    daysIn: 33,  added: "20 Feb 2026", karigar: null,           customer: null,         overdue: false },
  { id: 23, tag: "TG-2026-023", product: "Polki Choker Set",              cat: "Sets",     purity: "91.6", gwt: 45.8, swt: 2.2, nwt: 43.6, making: 1500, stoneVal: 185000, costRate: 8300, location: "Vault",    daysIn: 3,   added: "21 Mar 2026", karigar: null,           customer: null,         overdue: false },
  // ── Karigar ─────────────────────────────────────────────────────────────────
  { id: 7,  tag: "TG-2026-007", product: "Custom Kangan (Order)",         cat: "Bangles",  purity: "91.6", gwt: 32.0, swt: 0,   nwt: 31.2, making: 900,  stoneVal: 0,      costRate: 8180, location: "Karigar",  daysIn: 15,  added: "09 Mar 2026", karigar: "Raju Sonar",   customer: null,         overdue: false, jobType: "New Making",  assignedTo: "Vikram Shah"  },
  { id: 11, tag: "TG-2026-011", product: "Gold Bracelet Repair",          cat: "Bracelet", purity: "91.6", gwt: 18.4, swt: 0,   nwt: 17.9, making: 800,  stoneVal: 0,      costRate: 8200, location: "Karigar",  daysIn: 22,  added: "02 Mar 2026", karigar: "Sunil Kamath", customer: null,         overdue: false, jobType: "Repair",      assignedTo: "Priya Menon"  },
  { id: 24, tag: "TG-2026-024", product: "Gold Chain Repair",             cat: "Chain",    purity: "91.6", gwt: 8.8,  swt: 0,   nwt: 8.6,  making: 550,  stoneVal: 0,      costRate: 8250, location: "Karigar",  daysIn: 4,   added: "20 Mar 2026", karigar: "Raju Sonar",   customer: null,         overdue: false, jobType: "Soldering",   assignedTo: "Anil Desai"   },
  { id: 25, tag: "TG-2026-025", product: "Bangle Set Rhodium Polish",     cat: "Bangles",  purity: "75",   gwt: 24.6, swt: 0,   nwt: 23.8, making: 1100, stoneVal: 0,      costRate: 6800, location: "Karigar",  daysIn: 10,  added: "14 Mar 2026", karigar: "Prakash Gupta",customer: null,         overdue: false, jobType: "Polishing",   assignedTo: "Vikram Shah"  },
  { id: 26, tag: "TG-2026-026", product: "Custom Pendant Engraving",      cat: "Pendant",  purity: "91.6", gwt: 6.4,  swt: 0,   nwt: 6.2,  making: 1400, stoneVal: 18000,  costRate: 8300, location: "Karigar",  daysIn: 7,   added: "17 Mar 2026", karigar: "Sunil Kamath", customer: null,         overdue: false, jobType: "Engraving",   assignedTo: "Priya Menon"  },
  // ── Approval ────────────────────────────────────────────────────────────────
  { id: 10, tag: "TG-2026-010", product: "Gold Pendant Set",              cat: "Pendant",  purity: "91.6", gwt: 18.2, swt: 0,   nwt: 17.8, making: 1200, stoneVal: 0,      costRate: 8300, location: "Approval", daysIn: 7,   added: "17 Mar 2026", karigar: null,           customer: "Meena Patel",overdue: false, assignedTo: "Vikram Shah" },
  { id: 27, tag: "TG-2026-027", product: "Diamond Ring Ladies",           cat: "Rings",    purity: "75",   gwt: 6.8,  swt: 1.2, nwt: 5.6,  making: 1800, stoneVal: 65000,  costRate: 6900, location: "Approval", daysIn: 28,  added: "25 Feb 2026", karigar: null,           customer: "Sunita Joshi",overdue: false, assignedTo: "Priya Menon" },
  { id: 28, tag: "TG-2026-028", product: "Gold Necklace Heavy",           cat: "Necklace", purity: "91.6", gwt: 54.2, swt: 0,   nwt: 53.0, making: 1100, stoneVal: 0,      costRate: 8100, location: "Approval", daysIn: 35,  added: "18 Feb 2026", karigar: null,           customer: "Kavita Rane", overdue: true,  assignedTo: "Anil Desai"  },
];

const MOVE_HISTORY = {
  1:  [ { date: "09 Jan 2026", from: "Received", to: "Vault",    by: "Vikram Shah",   note: "New stock — Surat purchase batch" },
        { date: "15 Jan 2026", from: "Vault",    to: "Floor",    by: "Priya Menon",   note: "Moved to display for Makar Sankranti season" } ],
  7:  [ { date: "09 Mar 2026", from: "Received", to: "Floor",    by: "Anil Desai",    note: "" },
        { date: "09 Mar 2026", from: "Floor",    to: "Karigar",  by: "Vikram Shah",   note: "Custom order — Raju Sonar, size 2.6, expected back 20 Mar" } ],
  10: [ { date: "17 Mar 2026", from: "Received", to: "Floor",    by: "Priya Menon",   note: "" },
        { date: "18 Mar 2026", from: "Floor",    to: "Approval", by: "Vikram Shah",   note: "Trial for Meena Patel — return expected 22 Mar" } ],
  12: [ { date: "21 Oct 2025", from: "Received", to: "Floor",    by: "Anil Desai",    note: "Stock from Mumbai purchase trip — slow mover, consider discount" } ],
  28: [ { date: "18 Feb 2026", from: "Received", to: "Vault",    by: "Vikram Shah",   note: "Heavy necklace — secured in vault" },
        { date: "20 Feb 2026", from: "Vault",    to: "Floor",    by: "Priya Menon",   note: "" },
        { date: "18 Feb 2026", from: "Floor",    to: "Approval", by: "Anil Desai",    note: "Trial for Kavita Rane — return was due 05 Mar, overdue" } ],
};

function getMaterial(item) {
  if (item.purity === "—") return "Silver";
  if (item.stoneVal > 0)   return "Diamond";
  return "Gold";
}

function calcMkt(item, rates) {
  const rateKey = parseFloat(item.purity) >= 99 ? "24K" : parseFloat(item.purity) >= 91 ? "22K" : item.purity === "75" ? "18K" : null;
  const metalVal = rateKey ? item.nwt * rates[rateKey] : item.nwt * rates.silver;
  return metalVal + item.making * item.nwt + item.stoneVal;
}
function calcCost(item) {
  return item.nwt * item.costRate + item.making * item.nwt + item.stoneVal;
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function InventoryPage({ rates, locFilter = "All", setLocFilter }) {
  const [search, setSearch]     = useState("");
  const [catFilter, setCatFilter] = useState("All");
  const [matFilter, setMatFilter] = useState("All");
  const [sortCol, setSortCol]       = useState("daysIn");
  const [sortDir, setSortDir]       = useState("desc");
  const [selectedItem, setSelectedItem] = useState(null);
  const [showMove, setShowMove]     = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveDest, setMoveDest]     = useState("Floor");
  const [confirmMelt, setConfirmMelt] = useState(false);
  const searchRef = useRef(null);

  const R = rates || { "24K": 9240, "22K": 8470, "18K": 6930, silver: 107 };

  // ── Filtering + sorting ──
  const base = INV
    .filter(i => locFilter === "All" || i.location === locFilter)
    .filter(i => matFilter === "All" || getMaterial(i) === matFilter)
    .filter(i => catFilter === "All" || i.cat === catFilter)
    .filter(i => {
      if (!search) return true;
      const q = search.toLowerCase();
      return i.tag.toLowerCase().includes(q)
          || i.product.toLowerCase().includes(q)
          || i.cat.toLowerCase().includes(q)
          || (i.karigar   && i.karigar.toLowerCase().includes(q))
          || (i.customer  && i.customer.toLowerCase().includes(q));
    });

  // Pre-compute market value so it can be used for sorting and display
  const filtered = base
    .map(i => ({ ...i, _mktVal: calcMkt(i, R), _costVal: calcCost(i) }))
    .sort((a, b) => {
      if (!sortCol) return 0;
      const av = a[sortCol], bv = b[sortCol];
      if (typeof av === "number") return sortDir === "asc" ? av - bv : bv - av;
      return 0;
    });

  // ── Metrics ──
  const active        = INV.filter(i => !["Sold", "Melted"].includes(i.location));
  const totalMkt      = active.reduce((s, i) => s + calcMkt(i, R), 0);
  const totalCost     = active.reduce((s, i) => s + calcCost(i), 0);
  const unrealised    = totalMkt - totalCost;
  const approvalItems = INV.filter(i => i.location === "Approval");
  const approvalVal   = approvalItems.reduce((s, i) => s + calcMkt(i, R), 0);
  const overdueCount  = INV.filter(i => i.overdue).length;
  const karigarItems  = INV.filter(i => i.location === "Karigar");
  const karigarWt     = karigarItems.reduce((s, i) => s + i.nwt, 0);
  const agingCount    = INV.filter(i => i.daysIn >= 90 && i.location === "Floor").length;

  const cats = ["All", ...new Set(INV.map(i => i.cat))];
  // Counts respond to both location + material filters so cards show live numbers
  const catPool = INV
    .filter(i => locFilter === "All" || i.location === locFilter)
    .filter(i => matFilter === "All" || getMaterial(i) === matFilter);
  const catCounts = cats.reduce((acc, c) => {
    acc[c] = c === "All" ? catPool.length : catPool.filter(i => i.cat === c).length;
    return acc;
  }, {});

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  }

  // ── Sub-components ──
  function PurityBadge({ value }) {
    if (!value || value === "—") return <span style={{ color: T.textDim, fontSize: 10 }}>Silver</span>;
    const p = parseFloat(value);
    const [color, bg, label] = p >= 99 ? [T.green, T.greenLight, "24K"] : p >= 91 ? [T.amber, T.amberLight, "22K"] : [T.blue, T.blueLight, "18K"];
    return (
      <span style={{ background: bg, color, border: `0.5px solid ${color}30`, borderRadius: 5, fontSize: 9, fontWeight: 700, padding: "2px 6px" }}>
        {label}
      </span>
    );
  }

  function LocationBadge({ item }) {
    const key = item.overdue ? "Overdue" : item.location;
    const cfg = LOC[key] || LOC.Floor;
    const suffix = item.karigar ? ` · ${item.karigar.split(" ")[0]}` : item.customer ? ` · ${item.customer.split(" ")[0]}` : "";
    return (
      <span style={{ background: cfg.bg, color: cfg.color, border: `0.5px solid ${cfg.color}30`, borderRadius: 20, fontSize: 9, fontWeight: 600, padding: "2px 8px", whiteSpace: "nowrap" }}>
        {cfg.label}{suffix}
      </span>
    );
  }

  function SortArrow({ col }) {
    if (sortCol !== col) return <span style={{ opacity: 0.25 }}><HI icon={ArrowDown01Icon} size={9} /></span>;
    return (
      <span style={{ color: T.gold, display: "inline-flex", transform: sortDir === "asc" ? "rotate(180deg)" : "none" }}>
        <HI icon={ArrowDown01Icon} size={9} />
      </span>
    );
  }

  const TH = ({ label, col, align = "left", pl = 8 }) => (
    <th onClick={col ? () => toggleSort(col) : undefined}
      style={{ padding: `7px 8px 7px ${pl}px`, fontSize: 9, fontWeight: 600, color: T.textMuted, textAlign: align, borderBottom: `0.5px solid ${T.surfaceBorder}`, letterSpacing: 0.5, textTransform: "uppercase", whiteSpace: "nowrap", cursor: col ? "pointer" : "default", background: T.surfaceHigh, userSelect: "none", position: "sticky", top: 0, zIndex: 2 }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>{label}{col && <SortArrow col={col} />}</span>
    </th>
  );

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "row", overflow: "hidden", background: T.bg, position: "relative" }}>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* ── Page header — matches tab bar height/style ── */}
      <div style={{ height: 38, background: T.surfaceHigh, borderBottom: `0.5px solid ${T.surfaceBorder}`, display: "flex", alignItems: "center", padding: "0 20px", gap: 10, flexShrink: 0 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>
          {locFilter === "All" ? "All Stock" : locFilter}
        </span>
        <span style={{ fontSize: 10, color: T.textDim, background: T.surface, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 10, padding: "1px 7px" }}>
          {filtered.length} items
        </span>
        {catFilter !== "All" && (
          <span style={{ fontSize: 10, color: T.textMuted }}>· {catFilter}</span>
        )}
        <div style={{ marginLeft: "auto" }}>
          <Btn small variant="primary"><HI icon={AddCircleIcon} size={12} /> Add Stock</Btn>
        </div>
      </div>

      {/* ── Mini Dashboard ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", borderBottom: `0.5px solid ${T.surfaceBorder}`, flexShrink: 0 }}>
        {[
          {
            label: "Total Stock Value",
            value: fmt(totalMkt),
            sub: `${unrealised >= 0 ? "+" : ""}${fmt(unrealised)} unrealised gain`,
            subColor: unrealised >= 0 ? T.green : T.red,
            tip: "Market value at today's live gold rate",
          },
          {
            label: "Capital Locked",
            value: fmt(totalCost),
            sub: `${active.length} items in stock`,
            subColor: T.textDim,
            tip: "What you paid for current stock",
          },
          {
            label: "On Approval",
            value: fmt(approvalVal),
            sub: `${approvalItems.length} items${overdueCount ? ` · ${overdueCount} overdue` : ""}`,
            subColor: overdueCount > 0 ? T.red : T.textDim,
            tip: "Stock physically outside the store",
          },
          {
            label: "With Karigar",
            value: `${karigarWt.toFixed(1)} g`,
            sub: `${karigarItems.length} jobs · ${agingCount} items aging >90d`,
            subColor: agingCount > 0 ? T.amber : T.textDim,
            tip: "Gold weight currently out for work",
          },
        ].map(({ label, value, sub, subColor }, idx) => (
          <div key={label} style={{ padding: "16px 20px", borderRight: idx < 3 ? `0.5px solid ${T.surfaceBorder}` : "none" }}>
            <div style={{ fontSize: 9, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: T.text, fontFamily: "DM Serif Display, serif", lineHeight: 1 }}>{value}</div>
            <div style={{ fontSize: 10, color: subColor, marginTop: 6, lineHeight: 1 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* ── Material filter ── */}
      {(() => {
        const MATS = [
          { key: "All",     label: "All",     color: T.textMuted,  bg: T.surfaceHigh  },
          { key: "Gold",    label: "Gold",    color: T.amber,      bg: T.amberLight   },
          { key: "Silver",  label: "Silver",  color: "#64748B",    bg: "#F1F5F9"      },
          { key: "Diamond", label: "Diamond", color: T.purple,     bg: T.purpleLight  },
        ];
        const matCounts = MATS.reduce((acc, m) => {
          const pool = INV.filter(i => locFilter === "All" || i.location === locFilter);
          acc[m.key] = m.key === "All" ? pool.length : pool.filter(i => getMaterial(i) === m.key).length;
          return acc;
        }, {});
        return (
          <div style={{ padding: "8px 20px", borderBottom: `0.5px solid ${T.surfaceBorder}`, display: "flex", gap: 8, flexShrink: 0 }}>
            {MATS.map(({ key, label, color, bg }) => {
              const on = matFilter === key;
              return (
                <button key={key} onClick={() => setMatFilter(key)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: on ? 600 : 400, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.15s", background: on ? bg : "transparent", border: `0.5px solid ${on ? color : T.surfaceBorder}`, color: on ? color : T.textDim }}>
                  {key !== "All" && <div style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0 }} />}
                  {label}
                  <span style={{ fontSize: 9, opacity: 0.7 }}>{matCounts[key]}</span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* ── Category cards ── */}
      <div style={{ padding: "12px 20px", borderBottom: `0.5px solid ${T.surfaceBorder}`, display: "flex", gap: 8, overflowX: "auto", flexShrink: 0, scrollbarWidth: "thin" }}>
        {cats.map(cat => {
          const icon   = cat === "All" ? GridViewIcon : getCatIcon(cat);
          const count  = catCounts[cat] ?? 0;
          const on     = catFilter === cat;
          const dimmed = !on && count === 0;
          return (
            <button key={cat} onClick={() => !dimmed && setCatFilter(cat)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                padding: "12px 12px 8px", width: 72, flexShrink: 0,
                borderRadius: 10, cursor: dimmed ? "default" : "pointer",
                fontFamily: "DM Sans, sans-serif", transition: "all 0.18s",
                background: on ? T.goldLight : T.surface,
                border: `1px solid ${on ? T.gold : T.surfaceBorder}`,
                boxShadow: on ? `0 2px 10px ${T.gold}22` : "0 1px 3px rgba(15,23,42,0.04)",
                opacity: dimmed ? 0.35 : 1,
              }}>
              <div style={{ position: "relative" }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: on ? `${T.gold}20` : T.surfaceHigh,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: on ? T.gold : T.textDim, transition: "all 0.18s",
                }}>
                  <HI icon={icon} size={15} />
                </div>
                {count > 0 && (
                  <div style={{ position: "absolute", top: -4, right: -5, minWidth: 14, height: 14, borderRadius: 7, background: on ? T.gold : T.surfaceBorderHigh, fontSize: 8, fontWeight: 700, color: on ? "#fff" : T.textMuted, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px", lineHeight: 1 }}>
                    {count}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 10, fontWeight: on ? 600 : 400, color: on ? T.goldDark : T.textMuted, whiteSpace: "nowrap", lineHeight: 1.2, textAlign: "center" }}>
                {cat}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Table section (search + table/panel row) ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Search header — inside the table section, billing-style */}
        <div style={{ padding: "8px 20px", borderBottom: `0.5px solid ${T.surfaceBorder}`, flexShrink: 0 }}>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <span style={{ position: "absolute", left: 10, color: T.textMuted, pointerEvents: "none", display: "flex", alignItems: "center" }}>
              <HI icon={Search01Icon} size={13} />
            </span>
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Scan tag or search by product, karigar, customer…"
              style={{ width: "100%", background: T.surfaceHigh, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 7, padding: "8px 28px 8px 28px", fontSize: 12, color: T.text, fontFamily: "DM Sans, sans-serif", outline: "none", transition: "border-color 0.15s" }}
              onFocus={e => e.target.style.borderColor = T.gold}
              onBlur={e => e.target.style.borderColor = T.surfaceBorder}
            />
            {search && (
              <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, background: "none", border: "none", color: T.textDim, cursor: "pointer", display: "flex", padding: 2, borderRadius: 4 }}>
                <HI icon={Cancel01Icon} size={12} />
              </button>
            )}
          </div>
        </div>


      {/* ── Inventory Table ── */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "auto", padding: "0 20px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
          <thead>
            <tr style={{ background: T.surfaceHigh }}>
              <TH label="Tag No."      col={null}    />
              <TH label="Product"      col={null}    />
              <TH label="Purity"       col={null}    />
              <TH label="Gr. Wt"       col="gwt"     align="right" />
              <TH label="Net Wt"       col="nwt"     align="right" />
              <TH label="Making"       col="making"   align="right" />
              <TH label="Market Value" col="_mktVal" align="right" />
              <TH label="Location"     col={null}    />
              <TH label="Days In"      col="daysIn"  align="right" />
              <TH label=""             col={null}    />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ padding: "56px 20px", textAlign: "center" }}>
                  <div style={{ color: T.textDim, marginBottom: 8 }}><HI icon={PackageIcon} size={32} /></div>
                  <div style={{ fontSize: 13, color: T.textMuted }}>No items match your search or filters</div>
                </td>
              </tr>
            ) : filtered.map(item => {
              const mktVal  = item._mktVal;
              const costVal = item._costVal;
              const marginVal = ((mktVal - costVal) / costVal * 100).toFixed(1);
              const margin = `${parseFloat(marginVal) >= 0 ? "+" : ""}${marginVal}%`;
              const isSelected = selectedItem?.id === item.id;
              const icon = getCatIcon(item.cat);
              const isDead  = item.daysIn >= 150;
              const isAging = item.daysIn >= 90;

              return (
                <tr key={item.id}
                  className={isSelected ? "" : "row-hover"}
                  onClick={() => setSelectedItem(isSelected ? null : item)}
                  style={{ cursor: "pointer", background: isSelected ? `${T.gold}08` : "transparent", borderBottom: `0.5px solid ${T.surfaceBorder}`, animation: "fadeIn 0.2s ease" }}>

                  {/* Tag */}
                  <td style={{ padding: "12px 8px", fontSize: 10, fontWeight: 700, color: T.gold, fontFamily: "DM Mono, monospace", whiteSpace: "nowrap", verticalAlign: "top", borderBottom: `0.5px solid ${T.surfaceBorder}` }}>
                    {item.tag}
                  </td>

                  {/* Product */}
                  <td style={{ padding: "12px 8px", verticalAlign: "top", borderBottom: `0.5px solid ${T.surfaceBorder}` }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ width: 26, height: 26, borderRadius: 6, background: T.goldLight, border: `0.5px solid ${T.gold}20`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: T.gold }}>
                        <HI icon={icon} size={13} />
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: T.text, whiteSpace: "nowrap", lineHeight: 1 }}>{item.product}</div>
                        <div style={{ fontSize: 9, color: T.textDim, marginTop: 3, lineHeight: 1 }}>{item.cat}</div>
                      </div>
                    </div>
                  </td>

                  {/* Purity */}
                  <td style={{ padding: "12px 8px", verticalAlign: "top", borderBottom: `0.5px solid ${T.surfaceBorder}` }}><PurityBadge value={item.purity} /></td>

                  {/* Gross wt */}
                  <td style={{ padding: "12px 8px", fontSize: 12, color: T.textMuted, textAlign: "right", fontVariantNumeric: "tabular-nums", verticalAlign: "top", borderBottom: `0.5px solid ${T.surfaceBorder}` }}>{fmtW(item.gwt)}</td>

                  {/* Net wt */}
                  <td style={{ padding: "12px 8px", fontSize: 12, fontWeight: 600, color: T.text, textAlign: "right", fontVariantNumeric: "tabular-nums", verticalAlign: "top", borderBottom: `0.5px solid ${T.surfaceBorder}` }}>{fmtW(item.nwt)}</td>

                  {/* Making */}
                  <td style={{ padding: "12px 8px", fontSize: 11, color: T.textMuted, textAlign: "right", verticalAlign: "top", borderBottom: `0.5px solid ${T.surfaceBorder}` }}>
                    <div style={{ fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>{fmt(item.making * item.nwt)}</div>
                    <div style={{ fontSize: 9, color: T.textDim, marginTop: 3, lineHeight: 1 }}>₹{item.making}/g</div>
                  </td>

                  {/* Market value */}
                  <td style={{ padding: "12px 8px", textAlign: "right", whiteSpace: "nowrap", verticalAlign: "top", borderBottom: `0.5px solid ${T.surfaceBorder}` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: "DM Serif Display, serif", lineHeight: 1 }}>{fmt(mktVal)}</div>
                    <div style={{ fontSize: 9, color: parseFloat(marginVal) >= 0 ? T.green : T.red, marginTop: 3, lineHeight: 1 }}>{margin}</div>
                  </td>

                  {/* Location */}
                  <td style={{ padding: "12px 8px", verticalAlign: "top", borderBottom: `0.5px solid ${T.surfaceBorder}` }}><LocationBadge item={item} /></td>

                  {/* Days in */}
                  <td style={{ padding: "12px 8px", fontSize: 11, textAlign: "right", fontWeight: isDead || isAging ? 600 : 400, color: isDead ? T.red : isAging ? T.amber : T.textDim, verticalAlign: "top", borderBottom: `0.5px solid ${T.surfaceBorder}` }}>
                    {item.daysIn}d{isDead ? " ⚠" : ""}
                  </td>

                  {/* Actions */}
                  <td style={{ padding: "12px 8px 12px 4px", whiteSpace: "nowrap", verticalAlign: "top", borderBottom: `0.5px solid ${T.surfaceBorder}` }}>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                      <button onClick={e => { e.stopPropagation(); setMoveTarget(item); setMoveDest(item.location); setShowMove(true); }}
                        style={{ padding: "3px 8px", background: "transparent", border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 5, fontSize: 10, color: T.textMuted, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.15s" }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = T.gold; e.currentTarget.style.color = T.gold; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = T.surfaceBorder; e.currentTarget.style.color = T.textMuted; }}>
                        Move
                      </button>
                      {item.location === "Floor" && (
                        <button onClick={e => e.stopPropagation()}
                          style={{ padding: "3px 8px", background: "transparent", border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 5, fontSize: 10, color: T.textMuted, cursor: "pointer", fontFamily: "DM Sans, sans-serif", transition: "all 0.15s" }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = T.gold; e.currentTarget.style.color = T.gold; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = T.surfaceBorder; e.currentTarget.style.color = T.textMuted; }}>
                          Bill
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>{/* end Table section */}
      </div>{/* end left column */}

      {/* ── Detail Panel ── */}
      {selectedItem && (
        <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 340, background: T.surface, borderLeft: `0.5px solid ${T.surfaceBorder}`, display: "flex", flexDirection: "column", overflowY: "auto", zIndex: 10, animation: "slideIn 0.18s ease" }}>

          {/* Panel header */}
          <div style={{ padding: "14px 20px", borderBottom: `0.5px solid ${T.surfaceBorder}`, display: "flex", alignItems: "flex-start", gap: 10, flexShrink: 0 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.gold, fontFamily: "DM Mono, monospace", lineHeight: 1 }}>{selectedItem.tag}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginTop: 4, lineHeight: 1.2 }}>{selectedItem.product}</div>
            </div>
            <button onClick={() => setSelectedItem(null)} style={{ background: "none", border: "none", color: T.textDim, cursor: "pointer", padding: 4, display: "flex", borderRadius: 4, flexShrink: 0 }}>
              <HI icon={Cancel01Icon} size={14} />
            </button>
          </div>

          {/* Panel scroll area */}
          <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

            {/* Location */}
            <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {(() => { const key = selectedItem.overdue ? "Overdue" : selectedItem.location; const cfg = LOC[key] || LOC.Floor; const suffix = selectedItem.karigar ? ` · ${selectedItem.karigar}` : selectedItem.customer ? ` · ${selectedItem.customer}` : ""; return <span style={{ background: cfg.bg, color: cfg.color, border: `0.5px solid ${cfg.color}30`, borderRadius: 20, fontSize: 10, fontWeight: 600, padding: "3px 10px" }}>{cfg.label}{suffix}</span>; })()}
                {selectedItem.overdue && <span style={{ fontSize: 10, color: T.red, fontWeight: 600 }}>Return overdue</span>}
              </div>
              {selectedItem.jobType && <div style={{ fontSize: 11, color: T.textMuted }}>Job type: <strong style={{ color: T.text }}>{selectedItem.jobType}</strong></div>}
              {selectedItem.assignedTo && <div style={{ fontSize: 11, color: T.textMuted }}>Assigned by: <strong style={{ color: T.text }}>{selectedItem.assignedTo}</strong></div>}
            </div>

            {/* Inset separator */}
            <div style={{ height: "0.5px", background: T.surfaceBorder, margin: "0 0 16px" }} />

            {/* Specs */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 9, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>Specifications</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 12px" }}>
                {[
                  ["Category",   selectedItem.cat],
                  ["Purity",     selectedItem.purity === "—" ? "Silver" : `${selectedItem.purity === "91.6" ? "22K" : selectedItem.purity === "75" ? "18K" : "24K"} (${selectedItem.purity}%)`],
                  ["Gross Wt",   fmtW(selectedItem.gwt)],
                  ["Stone Wt",   selectedItem.swt > 0 ? fmtW(selectedItem.swt) : "—"],
                  ["Net Wt",     fmtW(selectedItem.nwt)],
                  ["Making",     `₹${selectedItem.making}/g`],
                  ["Stone Value",selectedItem.stoneVal > 0 ? fmt(selectedItem.stoneVal) : "—"],
                  ["Days in stock", `${selectedItem.daysIn} days`],
                  ["Added",      selectedItem.added],
                ].map(([l, v]) => (
                  <div key={l}>
                    <div style={{ fontSize: 9, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.4, lineHeight: 1 }}>{l}</div>
                    <div style={{ fontSize: 12, fontWeight: 500, color: T.text, marginTop: 3, lineHeight: 1 }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Inset separator */}
            <div style={{ height: "0.5px", background: T.surfaceBorder, margin: "0 0 16px" }} />

            {/* Valuation */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 9, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 10 }}>Live Valuation</div>
              {(() => {
                const rateKey = parseFloat(selectedItem.purity) >= 99 ? "24K" : parseFloat(selectedItem.purity) >= 91 ? "22K" : selectedItem.purity === "75" ? "18K" : null;
                const metalVal  = rateKey ? selectedItem.nwt * R[rateKey] : selectedItem.nwt * R.silver;
                const makingAmt = selectedItem.making * selectedItem.nwt;
                const total     = metalVal + makingAmt + selectedItem.stoneVal;
                const cost      = calcCost(selectedItem);
                return (
                  <div>
                    {[
                      ["Metal value", fmt(metalVal)],
                      ["Making charges", fmt(makingAmt)],
                      ...(selectedItem.stoneVal > 0 ? [["Stone / polish", fmt(selectedItem.stoneVal)]] : []),
                    ].map(([l, v]) => (
                      <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 11 }}>
                        <span style={{ color: T.textMuted }}>{l}</span>
                        <span style={{ color: T.text, fontWeight: 500 }}>{v}</span>
                      </div>
                    ))}
                    <div style={{ height: "0.5px", background: T.surfaceBorder, margin: "6px 0" }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>Market Value</span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: T.gold, fontFamily: "DM Serif Display, serif" }}>{fmt(total)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                      <span style={{ fontSize: 10, color: T.textDim }}>Your cost</span>
                      <span style={{ fontSize: 10, color: T.textDim }}>{fmt(cost)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                      <span style={{ fontSize: 10, color: T.textDim }}>Unrealised gain</span>
                      <span style={{ fontSize: 10, color: total > cost ? T.green : T.red, fontWeight: 600 }}>{total > cost ? "+" : ""}{fmt(total - cost)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Inset separator */}
            <div style={{ height: "0.5px", background: T.surfaceBorder, margin: "0 0 16px" }} />

            {/* Movement history */}
            <div>
              <div style={{ fontSize: 9, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 12 }}>Movement History</div>
              {(() => {
                const history = MOVE_HISTORY[selectedItem.id] || [{ date: selectedItem.added, from: "Received", to: selectedItem.location, by: "—", note: "" }];
                return history.map((m, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                      <div style={{ width: 7, height: 7, borderRadius: "50%", background: i === 0 ? T.gold : T.surfaceBorderHigh, marginTop: 3 }} />
                      {i < history.length - 1 && <div style={{ width: 1, flex: 1, background: T.surfaceBorder, marginTop: 3 }} />}
                    </div>
                    <div style={{ paddingBottom: 4 }}>
                      <div style={{ fontSize: 11, color: T.text, fontWeight: 500 }}>{m.from} → {m.to}</div>
                      <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>{m.date} · {m.by}</div>
                      {m.note && <div style={{ fontSize: 10, color: T.textMuted, marginTop: 3, fontStyle: "italic" }}>{m.note}</div>}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>

          {/* Panel actions */}
          <div style={{ padding: "12px 20px", borderTop: `0.5px solid ${T.surfaceBorder}`, display: "flex", gap: 8, flexShrink: 0 }}>
            <Btn small onClick={() => { setMoveTarget(selectedItem); setMoveDest(selectedItem.location); setShowMove(true); }}>Move Item</Btn>
            {selectedItem.location === "Floor" && <Btn small variant="primary"><HI icon={Invoice02Icon} size={12} /> Create Bill</Btn>}
          </div>
        </div>
      )}

      {/* ── Move Item Modal ── */}
      {showMove && moveTarget && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.35)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => { setShowMove(false); setConfirmMelt(false); }}>
          <div style={{ background: T.surface, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 10, width: 400, boxShadow: "0 8px 32px rgba(15,23,42,0.12)", padding: "20px 24px", animation: "fadeIn 0.15s ease" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 4 }}>Move Item</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>
              <span style={{ fontFamily: "DM Mono, monospace", color: T.gold, fontWeight: 700 }}>{moveTarget.tag}</span> · {moveTarget.product}
            </div>
            {/* Current location */}
            <div style={{ fontSize: 11, color: T.textDim, marginBottom: 20 }}>
              Currently: <span style={{ fontWeight: 600, color: T.textMuted }}>{moveTarget.location}</span>
              {moveTarget.karigar && <span> · {moveTarget.karigar}</span>}
              {moveTarget.customer && <span> · {moveTarget.customer}</span>}
            </div>

            {confirmMelt ? (
              /* Melt confirmation step */
              <div style={{ marginBottom: 20 }}>
                <div style={{ background: T.redLight, border: `0.5px solid ${T.red}40`, borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.red, marginBottom: 4 }}>Confirm melt / scrap</div>
                  <div style={{ fontSize: 11, color: T.red }}>This cannot be undone. The item will be marked as melted and removed from active stock.</div>
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <Btn onClick={() => { setConfirmMelt(false); setMoveDest(moveTarget.location); }}>Back</Btn>
                  <Btn variant="danger" onClick={() => { setShowMove(false); setConfirmMelt(false); }}>Yes, Melt Item</Btn>
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 9, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Move to location</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
                  {[
                    { dest: "Floor",    desc: "Showroom display"    },
                    { dest: "Vault",    desc: "Safe storage"        },
                    { dest: "Karigar",  desc: "Out for work"        },
                    { dest: "Approval", desc: "With customer"       },
                    { dest: "Melt",     desc: "Melt / scrap"        },
                  ].map(({ dest, desc }) => {
                    const isCurrent = dest === moveTarget.location;
                    const isMelt    = dest === "Melt";
                    const isSelected = moveDest === dest;
                    return (
                      <button key={dest} onClick={() => !isCurrent && setMoveDest(dest)}
                        disabled={isCurrent}
                        style={{ padding: "10px 14px", border: `0.5px solid ${isMelt && isSelected ? T.red : isSelected ? T.gold : T.surfaceBorder}`, borderRadius: 8, background: isCurrent ? T.surfaceHigh : isMelt && isSelected ? T.redLight : isSelected ? T.goldLight : T.surface, color: isCurrent ? T.textDim : isMelt && isSelected ? T.red : isSelected ? T.gold : T.text, fontSize: 12, fontWeight: isSelected ? 600 : 400, cursor: isCurrent ? "not-allowed" : "pointer", fontFamily: "DM Sans, sans-serif", textAlign: "left", transition: "all 0.15s", opacity: isCurrent ? 0.5 : 1 }}>
                        <div>{dest}{isCurrent && <span style={{ fontSize: 9, color: T.textDim }}> (current)</span>}</div>
                        <div style={{ fontSize: 10, color: isMelt && isSelected ? T.red : isSelected ? T.goldMid : T.textDim, marginTop: 2 }}>{desc}</div>
                      </button>
                    );
                  })}
                </div>

                {(moveDest === "Karigar" || moveDest === "Approval") && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 9, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
                      {moveDest === "Karigar" ? "Karigar name" : "Customer name"}
                    </div>
                    <input placeholder={moveDest === "Karigar" ? "e.g. Raju Sonar" : "e.g. Meena Patel"}
                      style={{ width: "100%", background: T.surfaceHigh, border: `0.5px solid ${T.surfaceBorder}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: T.text, fontFamily: "DM Sans, sans-serif", outline: "none", boxSizing: "border-box" }} />
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <Btn onClick={() => setShowMove(false)}>Cancel</Btn>
                  {moveDest === "Melt"
                    ? <Btn variant="danger" onClick={() => setConfirmMelt(true)}>Proceed to Melt…</Btn>
                    : <Btn variant="primary" onClick={() => setShowMove(false)} style={{ opacity: moveDest === moveTarget.location ? 0.4 : 1 }}>Confirm Move</Btn>
                  }
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
